import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loadApiList, setAppInitialized, setMultiModelData, setModelInfo, replaceAttrs, setPersistLoaded, setModelDataRestored } from 'store/base/commonSlice';
import { setShareGroups } from 'store/auth/userSlice';
import { callApi } from 'services/ApiCommon';
import { fetchShareGroups } from 'services/CloudApiService';
import useAuth from 'utils/hooks/useAuth';
import { getConfig } from 'configs/edition.config';
import { v4 as uuidv4 } from 'uuid';
import { modelDataDB } from 'utils/modelDataDB';
import { applySessionCommUse } from 'services/commClient';

/**
 * 앱 초기화를 담당하는 중앙화된 컴포넌트
 *
 * 초기화 순서:
 * 1. 사용자 정보 확인 (userId)
 * 2. 세션 유효성 확인 (keep-session API)
 * 3. API 목록 로드
 * 4. 사용자 메뉴 로드
 *
 * 각 단계 실패 시 즉시 로그아웃 처리
 */
const AppInitializer = ({ onInitialized }) => {
    const dispatch = useDispatch();
    const { signOut } = useAuth();
    const userId = useSelector((state) => state.auth?.user?.userId);
    const { apiListLoaded } = useSelector((state) => state.base.common);

    // Standalone 모드 확인
    const editionConfig = useMemo(() => getConfig(), []);
    const isStandalone = editionConfig.isStandalone;

    const [isInitializing, setIsInitializing] = useState(false);
    const [initializationComplete, setInitializationComplete] = useState(false);
    const hasInitializedRef = useRef(false);
    const [logoutModal, setLogoutModal] = useState(null); // { message, onConfirm }

    // 현재 초기화 단계 (순차적 표시용)
    const [currentStep, setCurrentStep] = useState({ step: 0, total: 6, message: '초기화 준비 중...' });

    // 재로그인 감지용 ref
    const lastUserIdRef = useRef(userId);

    // 재로그인 시 초기화 상태 리셋
    useEffect(() => {
        const handleUserLoggedIn = (event) => {
            hasInitializedRef.current = false;
            setInitializationComplete(false);
        };

        window.addEventListener('user-logged-in', handleUserLoggedIn);
        return () => {
            window.removeEventListener('user-logged-in', handleUserLoggedIn);
        };
    }, []);

    // userId가 변경되면 (재로그인 시) 초기화 상태 리셋
    useEffect(() => {
        if (userId && lastUserIdRef.current !== userId) {
            hasInitializedRef.current = false;
            setInitializationComplete(false);
            lastUserIdRef.current = userId;
        }
    }, [userId]);

    // Standalone 모드에서는 초기화 건너뛰고 바로 완료 처리 + 샘플 모델 로드
    useEffect(() => {
        if (isStandalone && !hasInitializedRef.current) {
            hasInitializedRef.current = true;
            setInitializationComplete(true);
            dispatch(setAppInitialized(true));

            // 샘플 모델 자동 로드
            loadSampleModels();

            if (onInitialized) {
                onInitialized();
            }
        }
    }, [isStandalone, dispatch, onInitialized]);

    /**
     * Standalone 모드에서 샘플 모델 자동 로드
     */
    /**
     * 모델이 없을 때 사용할 빈 신규 모델 생성
     */
    const createEmptyModel = () => {
        const key = uuidv4();
        return {
            key,
            modelName: '새모델',
            modelDescription: '',
            projectNm: '',
            nodes: [],
            edges: [],
            attrs: [],
            openMethod: '신규',
            isModified: false,
        };
    };

    /**
     * 빈 신규 모델 1개를 자동 생성하여 활성화 (모델이 전혀 없을 때)
     */
    const initWithEmptyModel = () => {
        const empty = createEmptyModel();
        dispatch(setMultiModelData({ [empty.key]: empty }));
        dispatch(setModelInfo(empty));
        dispatch(replaceAttrs([]));
        dispatch(setPersistLoaded(true));
        dispatch(setModelDataRestored(true));
    };

    const loadSampleModels = async () => {
        try {
            dispatch(setModelDataRestored(false));

            // Electron API 확인
            if (!window.electronAPI?.getSampleModels) {
                initWithEmptyModel();
                return;
            }

            const result = await window.electronAPI.getSampleModels();

            if (!result.success || !result.models || result.models.length === 0) {
                initWithEmptyModel();
                return;
            }


            let firstModel = null;
            const allModels = [];

            // 샘플 모델 필터링: 수정된 모델이나 빈 모델(현재 모델 상태) 제외
            const shouldSkipModel = (model) => {
                // isModified가 true인 모델 제외
                if (model.isModified === true) return true;
                // nodes와 edges가 모두 비어있는 빈 모델(새모델 등) 제외
                const hasNoNodes = !model.nodes || model.nodes.length === 0;
                const hasNoEdges = !model.edges || model.edges.length === 0;
                if (hasNoNodes && hasNoEdges) return true;
                return false;
            };

            // 각 샘플 파일 처리
            for (const sampleFile of result.models) {
                try {
                    if (sampleFile.isMultiModel && sampleFile.data.models) {
                        // 다중 모델 파일
                        for (const model of sampleFile.data.models) {
                            if (shouldSkipModel(model)) {
                                continue;
                            }
                            const modelData = {
                                key: model.key || uuidv4(),
                                modelName: model.modelName || '샘플 모델',
                                modelDescription: model.modelDescription || '',
                                projectNm: model.projectNm || sampleFile.fileName.replace('.json', ''),
                                nodes: model.nodes || [],
                                edges: model.edges || [],
                                attrs: model.attrs || [],
                                isModified: false,
                                isSample: true
                            };
                            allModels.push(modelData);
                            if (!firstModel) firstModel = modelData;
                        }
                    } else {
                        // 단일 모델 파일
                        const model = sampleFile.data;
                        if (shouldSkipModel(model)) {
                            continue;
                        }
                        const modelData = {
                            key: model.key || uuidv4(),
                            modelName: model.modelName || sampleFile.fileName.replace('.json', ''),
                            modelDescription: model.modelDescription || '',
                            projectNm: model.projectNm || sampleFile.fileName.replace('.json', ''),
                            nodes: model.nodes || [],
                            edges: model.edges || [],
                            attrs: model.attrs || [],
                            isModified: false,
                            isSample: true
                        };
                        allModels.push(modelData);
                        if (!firstModel) firstModel = modelData;
                    }
                } catch (modelError) {
                    console.error(`📂 샘플 모델 처리 오류 (${sampleFile.fileName}):`, modelError);
                }
            }

            if (allModels.length > 0) {

                // 🔧 FIX: 배열을 객체(Record<string, ModelInfo>)로 변환
                const allModelsObj = {};
                allModels.forEach(model => {
                    const key = model.key || model.mdlrmdlId;
                    if (key) {
                        allModelsObj[key] = model;
                    }
                });
                dispatch(setMultiModelData(allModelsObj));

                // 첫 번째 모델을 현재 모델로 설정
                if (firstModel) {
                    dispatch(setModelInfo(firstModel));
                    dispatch(replaceAttrs(firstModel.attrs || []));
                }

                // Standalone 모드에서 persistLoaded 및 modelDataRestored를 true로 설정하여 ReactFlow 렌더링 활성화
                dispatch(setPersistLoaded(true));
                dispatch(setModelDataRestored(true));
            } else {
                // 샘플은 있었으나 모두 필터링되어 0개인 경우 → 빈 신규 모델 자동 생성
                initWithEmptyModel();
            }
        } catch (error) {
            console.error("❌ AppInitializer - 샘플 모델 로드 오류:", error);
            // 오류 발생 시에도 빈 신규 모델로 복구
            try { initWithEmptyModel(); } catch (_) {}
        }
    };

    useEffect(() => {
        // Standalone 모드는 위 useEffect에서 처리
        if (isStandalone) {
            return;
        }


        // 이미 초기화되었거나 초기화 중이면 중단
        if (hasInitializedRef.current || isInitializing) {
            return;
        }

        // userId가 없으면 초기화하지 않음 (ProtectedRoute가 로그인 페이지로 리다이렉트)
        if (!userId) {
            return;
        }

        // 이미 API가 로드되었으면 초기화 완료로 처리
        if (apiListLoaded) {
            setInitializationComplete(true);
            if (onInitialized) onInitialized();
            return;
        }

        const initializeApp = async () => {
            hasInitializedRef.current = true;
            setIsInitializing(true);

            // 🔧 FIX: 초기화 시작 시 modelDataRestored를 false로 리셋 (race condition 방지)
            // DiagramMain이 이 플래그를 확인하여 데이터가 준비될 때까지 렌더링을 지연함
            dispatch(setModelDataRestored(false));


            try {
                // ============================================
                // Step 1: 사용자 정보 확인
                // ============================================
                setCurrentStep({ step: 1, total: 6, message: '사용자 정보 확인 중...' });
                if (!userId) {
                    console.error("❌ 사용자 정보 없음");
                    await handleLogout('사용자 정보를 찾을 수 없습니다. 다시 로그인해주세요.');
                    return;
                }

                // ============================================
                // Step 2: 세션 유효성 확인
                // ============================================
                setCurrentStep({ step: 2, total: 6, message: '세션 확인 중...' });
                const sessionValid = await checkSession();
                if (!sessionValid) {
                    console.error("❌ 세션이 만료되었거나 유효하지 않음");
                    await handleLogout('세션이 만료되었습니다. 다시 로그인해주세요.');
                    return;
                }

                // ============================================
                // Step 3: API 목록 로드
                // ============================================
                setCurrentStep({ step: 3, total: 6, message: 'API 목록 로드 중...' });
                const apiList = await dispatch(loadApiList()).unwrap();

                if (!apiList || apiList.length === 0) {
                    console.error("❌ API 목록이 비어있음");
                    await handleLogout('API 목록을 로드할 수 없습니다. 관리자에게 문의하세요.');
                    return;
                }

                // ============================================
                // Step 4: 사용자 메뉴 로드
                // ============================================
                setCurrentStep({ step: 4, total: 6, message: '메뉴 로드 중...' });
                const menuList = await loadUserMenu(userId);

                if (!menuList || menuList.length === 0) {
                    console.error("❌ 메뉴 목록이 비어있음");
                    await handleLogout('메뉴 목록을 로드할 수 없습니다. 관리자에게 문의하세요.');
                    return;
                }

                // ============================================
                // Step 5: 공유그룹 로드 (선택적 - 실패해도 앱 사용 가능)
                // ============================================
                setCurrentStep({ step: 5, total: 6, message: '공유그룹 로드 중...' });
                try {
                    const shareGroupList = await fetchShareGroups(userId);
                    dispatch(setShareGroups(shareGroupList || []));
                } catch (shareGroupError) {
                    console.warn("⚠️ 공유그룹 로드 실패 (앱 사용에는 영향 없음):", shareGroupError);
                    // 공유그룹 로드 실패는 앱 사용에 영향 없으므로 무시
                }

                // ============================================
                // Step 6: IndexedDB에서 모델 데이터 복구
                // ============================================
                setCurrentStep({ step: 6, total: 6, message: '모델 데이터 복구 중...' });
                try {
                    const savedModels = await modelDataDB.loadModels();
                    if (savedModels && savedModels.multiModelData?.length > 0) {
                        // 🔧 FIX: 배열을 객체(Record<string, ModelInfo>)로 변환하여 중복 모델 방지
                        // ⚠️ 모델 자체에도 `key` 필드를 강제 보장: setModelInfo가 추후 호출될 때
                        // payload.key가 비어있으면 신규 uid를 생성하고 multiModelData[새키]에 자동
                        // 동기화되어 복제가 발생함 (commonSlice.ts setModelInfo 동작).
                        const multiModelDataObj = {};
                        savedModels.multiModelData.forEach(model => {
                            const key = model.key || model.mdlrmdlId;
                            if (key) {
                                multiModelDataObj[key] = { ...model, key };
                            }
                        });
                        dispatch(setMultiModelData(multiModelDataObj));

                        // 현재 활성 모델 복구
                        let modelToActivate = null;

                        if (savedModels.currentModelKey) {
                            modelToActivate = multiModelDataObj[savedModels.currentModelKey];
                        }

                        // currentModelKey로 못 찾았거나 없으면 첫 번째 모델 사용
                        if (!modelToActivate) {
                            const modelKeys = Object.keys(multiModelDataObj);
                            if (modelKeys.length > 0) {
                                modelToActivate = multiModelDataObj[modelKeys[0]];
                            }
                        }

                        if (modelToActivate) {
                            dispatch(setModelInfo(modelToActivate));
                            // attrs가 배열인지 확인
                            const attrs = Array.isArray(modelToActivate.attrs) ? modelToActivate.attrs : [];
                            dispatch(replaceAttrs(attrs));
                        }
                    } else {
                    }
                } catch (modelError) {
                    console.warn("⚠️ 모델 데이터 복구 실패 (앱 사용에는 영향 없음):", modelError);
                }

                // 🔧 FIX: IndexedDB 모델 데이터 복원 완료 플래그 설정 (race condition 방지)
                // DiagramMain이 이 플래그를 확인하여 데이터가 준비된 후에만 렌더링함
                dispatch(setModelDataRestored(true));

                // ============================================
                // 초기화 완료
                // ============================================
                setInitializationComplete(true);

                // 애플리케이션 초기화 완료 플래그 설정 (히스토리 복원 다이얼로그 제어용)
                dispatch(setAppInitialized(true));

                // 초기화 완료 이벤트 발행
                window.dispatchEvent(new CustomEvent('app-initialized', {
                    detail: {
                        userId,
                        apiCount: apiList.length,
                        menuCount: menuList.length,
                        timestamp: new Date().toISOString()
                    }
                }));

                if (onInitialized) {
                    onInitialized();
                }

            } catch (error) {
                console.error("❌ AppInitializer - 초기화 중 오류:", error);

                const errorMessage = error?.message || String(error);

                // 인증 관련 오류
                if (errorMessage.includes('401') ||
                    errorMessage.includes('403') ||
                    errorMessage.includes('Unauthorized') ||
                    errorMessage.includes('Forbidden')) {
                    await handleLogout('세션이 만료되었습니다. 다시 로그인해주세요.');
                } else {
                    // 기타 오류 - 재시도 가능하도록 플래그 리셋
                    console.warn("⚠️ 일시적 오류 발생. 재시도 가능 상태로 설정합니다.");
                    hasInitializedRef.current = false;
                    await handleLogout('초기화 중 오류가 발생했습니다. 다시 로그인해주세요.');
                }
            } finally {
                setIsInitializing(false);
            }
        };

        initializeApp();
    }, [userId, apiListLoaded, dispatch, signOut, onInitialized, isInitializing, isStandalone]);

    /**
     * 세션 유효성 확인
     */
    /**
     * 작업 시스템을 확정한다 — 데이터모델러는 시스템 **선택 화면이 없다**.
     *
     * QBase 백엔드의 범위 판정(읽기 스코프·AI 동의·한도·과금 귀속)은 전부 `activeSysId` 축 위에 있는데,
     * 로그인만으로는 그 축이 채워지지 않는다(`qbase.scope.session-mode=ACTIVE` 인 환경).
     * 그래서 **계정 소속(homeSysId)을 그대로 작업 시스템으로 선택**한다 — 모델러 사용자는
     * 자기 시스템 하나만 쓰므로 고를 것이 없다.
     *
     * 로그인 경로가 5개(일반·구글·네이버·구글가입·네이버가입)라 각각에 넣으면 하나를 빠뜨린다.
     * 세션 확인은 **경로와 무관하게, 새로고침 후에도** 도는 공통 지점이라 여기에 둔다.
     *
     * 실패해도 앱을 막지 않는다 — 서버가 목록 밖 값을 거부하는 것이 정상 동작이고,
     * 그때는 해당 기능이 자기 사유("작업할 시스템을 먼저 선택하세요")로 실패한다.
     */
    const ensureActiveSystem = async (sessionData) => {
        const homeSysId = sessionData?.homeSysId || sessionData?.user?.homeSysId;
        if (!homeSysId) {
            console.warn("[세션] homeSysId 가 없어 작업 시스템을 선택하지 못했습니다.");
            return;
        }
        if (sessionData?.activeSysId === homeSysId) return;   // 이미 선택돼 있으면 건너뛴다

        try {
            const res = await fetch(
                `${process.env.REACT_APP_BE_API}/qbase/api/session/select-system`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ qbaseSysId: homeSysId })
                }
            );
            if (res.ok) {
            } else {
                console.warn("[세션] 작업 시스템 선택 실패:", res.status);
            }
        } catch (e) {
            console.warn("[세션] 작업 시스템 선택 중 오류:", e.message);
        }
    };

    const checkSession = async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10초 타임아웃

        try {
            // keep-session API를 사용하여 세션 확인
            // 이 API는 세션을 갱신하면서 동시에 유효성도 확인함
            const response = await fetch(
                `${process.env.REACT_APP_BE_API}/qbase/api/keep-session`,
                {
                    method: 'GET',
                    credentials: 'include', // 쿠키 포함
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    signal: controller.signal
                }
            );
            clearTimeout(timeoutId);

            if (response.ok) {
                const data = await response.json();
                applySessionCommUse(data);
                await ensureActiveSystem(data);
                return true;
            } else if (response.status === 401 || response.status === 403) {
                console.error("세션 인증 실패:", response.status);
                return false;
            } else {
                console.warn("세션 확인 응답 이상:", response.status);
                // 기타 오류는 일단 세션이 유효한 것으로 간주하고 다음 단계 진행
                return true;
            }
        } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                console.warn("⚠️ 세션 확인 타임아웃 (10초), 세션 유효한 것으로 간주하고 다음 단계 진행");
                return true;
            }
            console.error("세션 확인 중 오류:", error);
            // 네트워크 오류 등은 일단 세션이 유효한 것으로 간주
            // API 로드 단계에서 다시 확인됨
            return true;
        }
    };

    /**
     * 사용자 메뉴 로드
     */
    const loadUserMenu = async (userId) => {
        try {
            const userMenus = await callApi('selectUserMnu', { usrid: userId });
            return userMenus;
        } catch (error) {
            console.error("사용자 메뉴 로드 실패:", error);
            throw error;
        }
    };

    /**
     * 로그아웃 처리
     */
    const handleLogout = async (message) => {
        console.error("🚪 로그아웃 처리:", message);

        // 사용자에게 알림 (커스텀 모달)
        if (message) {
            await new Promise(resolve => {
                setLogoutModal({ message, onConfirm: resolve });
            });
        }

        try {
            await signOut();
        } catch (error) {
            console.error("로그아웃 처리 중 오류:", error);
            // 로그아웃 API 실패해도 클라이언트 상태는 정리됨
        }
    };

    // 초기화 중이면 로딩 표시 (순차적 단계 표시)
    if (isInitializing) {
        const progressPercent = Math.round((currentStep.step / currentStep.total) * 100);

        return (
            <>
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
            }}>
                {/* 스피너 */}
                <div style={{
                    width: '50px',
                    height: '50px',
                    border: '5px solid #f3f3f3',
                    borderTop: '5px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '24px'
                }} />

                {/* 현재 단계 메시지 */}
                <div style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    marginBottom: '16px',
                    color: '#333'
                }}>
                    {currentStep.message}
                </div>

                {/* 진행률 바 */}
                <div style={{
                    width: '250px',
                    height: '6px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '3px',
                    overflow: 'hidden',
                    marginBottom: '8px'
                }}>
                    <div style={{
                        width: `${progressPercent}%`,
                        height: '100%',
                        backgroundColor: '#3498db',
                        borderRadius: '3px',
                        transition: 'width 0.3s ease'
                    }} />
                </div>

                {/* 단계 표시 */}
                <div style={{
                    fontSize: '12px',
                    color: '#9ca3af'
                }}>
                    {currentStep.step} / {currentStep.total}
                </div>

                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
            {logoutModal && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000
            }}>
                <div style={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    padding: '28px 32px',
                    minWidth: '320px',
                    maxWidth: '420px',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
                    textAlign: 'center'
                }}>
                    <div style={{
                        fontSize: '15px',
                        color: '#374151',
                        marginBottom: '24px',
                        lineHeight: '1.6'
                    }}>
                        {logoutModal.message}
                    </div>
                    <button
                        onClick={() => {
                            const confirm = logoutModal.onConfirm;
                            setLogoutModal(null);
                            confirm();
                        }}
                        style={{
                            backgroundColor: '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 28px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer'
                        }}
                    >
                        확인
                    </button>
                </div>
            </div>
            )}
            </>
        );
    }

    // 로그아웃 알림 모달 (isInitializing 아닐 때)
    if (logoutModal) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000
            }}>
                <div style={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    padding: '28px 32px',
                    minWidth: '320px',
                    maxWidth: '420px',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
                    textAlign: 'center'
                }}>
                    <div style={{
                        fontSize: '15px',
                        color: '#374151',
                        marginBottom: '24px',
                        lineHeight: '1.6'
                    }}>
                        {logoutModal.message}
                    </div>
                    <button
                        onClick={() => {
                            const confirm = logoutModal.onConfirm;
                            setLogoutModal(null);
                            confirm();
                        }}
                        style={{
                            backgroundColor: '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 28px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer'
                        }}
                    >
                        확인
                    </button>
                </div>
            </div>
        );
    }

    // 초기화 완료되면 아무것도 렌더링하지 않음
    return null;
};

export default AppInitializer;
